/**
 * Progress Row Animation
 * Fills the progress bar and counts the adjacent value up from 0 when it enters the viewport.
 */
(() => {
  const DURATION = 1200;

  // Matches an optional sign, an integer part, an optional Italian-style decimal (comma), and any trailing suffix (%, /cm², ...).
  const VALUE_PATTERN = /^([+-]?)(\d+)(?:,(\d+))?(.*)$/;

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const parseTarget = (text) => {
    const match = text.trim().match(VALUE_PATTERN);
    if (!match) return null;
    const [, sign, intPart, decPart] = match;
    const suffix = match[4];
    return {
      sign,
      suffix,
      decimals: decPart ? decPart.length : 0,
      value: parseFloat(`${intPart}.${decPart || 0}`),
    };
  };

  const formatValue = (current, target) => {
    const formatted = current.toFixed(target.decimals).replace('.', ',');
    const sign = current === 0 ? '' : target.sign;
    return `${sign}${formatted}${target.suffix}`;
  };

  const animateValue = (el, target) => {
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / DURATION, 1);
      const eased = easeOutCubic(progress);
      el.textContent = formatValue(target.value * eased, target);

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  const prepareCounter = (triggerEl, valueEl) => {
    if (!valueEl || triggerEl.dataset.progressInit) return;
    triggerEl.dataset.progressInit = 'true';

    const target = parseTarget(valueEl.textContent);
    if (!target) return;

    triggerEl.progressTarget = target;
    triggerEl.progressValueEl = valueEl;
    valueEl.textContent = formatValue(0, target);
  };

  const initProgressRows = () => {
    // Bars: fill + count up the adjacent value (sibling .text-block p).
    const rows = document.querySelectorAll('[data-progress-row]');
    // Standalone numbers (no bar): count up the element itself.
    const counters = document.querySelectorAll('[data-progress-counter]');
    if (rows.length === 0 && counters.length === 0) return;

    rows.forEach((row) => prepareCounter(row, row.parentElement?.querySelector('.text-block p')));
    counters.forEach((el) => prepareCounter(el, el));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            if (entry.target.progressTarget && entry.target.progressValueEl) {
              animateValue(entry.target.progressValueEl, entry.target.progressTarget);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    rows.forEach((row) => observer.observe(row));
    counters.forEach((el) => observer.observe(el));
  };

  initProgressRows();
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', initProgressRows);
  }
})();
